import * as React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import { Grid, CircularProgress, Autocomplete } from "@mui/material";
import Swal from "sweetalert2";
import { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../../../../api";

const CURRENCIES = ["USD", "EUR", "GBP", "INR", "AUD", "MMK"];
const TYPES = ["Medicine", "Treatment", "Package"];

export default function PointRulesDialog({
  open,
  close,
  getPointRules,
  editData,
}) {
  const token = localStorage.getItem("token");
  const url = API_URL();
  const api = axios.create({
    baseURL: url,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const [point, setPoint] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState("Product");
  const [currency, setCurrency] = useState("MMK");
  const [relatedShop, setRelatedShop] = useState(null);
  const [category, setCategory] = useState("");

  const [shops, setShops] = useState([]);
  const [shopsLoading, setShopsLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchShops = async () => {
    setShopsLoading(true);
    try {
      const res = await api.get("v2/shop", { params: { limit: 99 } });
      const list = res.data?.data || res.data?.shops || [];
      setShops(list);
    } catch {
      setShops([]);
    } finally {
      setShopsLoading(false);
    }
  };

  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const res = await api.get("body-parts");
      const list = res.data?.data || res.data?.list || [];
      setCategories(list);
    } catch {
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchShops();
      fetchCategories();
    }
  }, [open]);

  useEffect(() => {
    if (editData) {
      setPoint(editData.point ?? "");
      setAmount(editData.amount ?? "");
      setDescription(editData.description || "");
      setType(editData.type || "Product");
      setCurrency(editData.currency || "MMK");
      setRelatedShop(editData.relatedShop || null);
      setCategory(editData.category || "");
    } else {
      resetForm();
    }
  }, [editData, open]);

  const resetForm = () => {
    setPoint("");
    setAmount("");
    setDescription("");
    setType("Product");
    setCurrency("MMK");
    setRelatedShop(null);
    setCategory("");
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    close();
  };

  const onSubmit = (e) => {
    e.preventDefault();
    let required = {};
    if (point === "" || Number(point) <= 0)
      required.point = "Valid point value is required";
    if (amount === "" || Number(amount) <= 0)
      required.amount = "Valid amount is required";
    if (!type) required.type = "Type is required";
    if (!currency) required.currency = "Currency is required";
    if (!relatedShop) required.relatedShop = "Related Shop is required";
    if (type === "Treatment" && !category)
      required.category = "Category is required";

    if (Object.keys(required).length > 0) {
      setErrors(required);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    const payload = {
      point: Number(point),
      amount: Number(amount),
      description,
      type,
      currency,
      relatedShop: relatedShop?._id || relatedShop?.id || relatedShop || null,
    };

    if (type === "Treatment") {
      payload.category = category;
    }

    const request = editData
      ? api.patch(`point-rules/${editData._id}`, payload)
      : api.post("point-rules", payload);

    request
      .then(() => {
        Swal.fire({
          title: "Successful!",
          text: editData
            ? "Point Rule Updated Successfully!"
            : "Point Rule Created Successfully!",
          icon: "success",
          cancelButtonText: "Close",
        });
        getPointRules();
        handleClose();
      })
      .catch((err) => {
        const backendErrors = err?.response?.data?.errors;
        if (backendErrors && typeof backendErrors === "object") {
          // Map backend field errors into inline helperText
          setErrors((prev) => ({ ...prev, ...backendErrors }));
          // Build a readable list for Swal
          const errorList = Object.values(backendErrors)
            .map((msg) => `<li>${msg}</li>`)
            .join("");
          Swal.fire({
            title: "Validation Error",
            html: `<ul style="text-align:left;padding-left:1.2rem;margin:0">${errorList}</ul>`,
            icon: "error",
            confirmButtonText: "OK",
          });
        } else {
          Swal.fire({
            title: "Something Went Wrong!",
            text: err?.response?.data?.message || "Error saving point rule",
            icon: "error",
            confirmButtonText: "Close",
          });
        }
      })
      .finally(() => setIsSubmitting(false));
  };

  const getShopLabel = (shop) =>
    shop?.name || shop?.shopName || shop?._id || "";

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          fontWeight: "bold",
          borderBottom: "1px solid #eee",
          mb: 2,
          fontSize: "1.25rem",
          color: "#333",
        }}
      >
        {editData ? "Update Point Rule" : "Create Point Rule"}
      </DialogTitle>

      <DialogContent sx={{ pt: "20px !important" }}>
        <form onSubmit={onSubmit} id="point-rule-form">
          <Grid container spacing={2.5}>
            {/* Point */}
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="Points"
                type="number"
                variant="outlined"
                fullWidth
                value={point}
                onChange={(e) => setPoint(e.target.value)}
                error={!!errors?.point}
                helperText={errors?.point}
                inputProps={{ min: 0 }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Grid>

            {/* Amount */}
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="Amount"
                type="number"
                variant="outlined"
                fullWidth
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                error={!!errors?.amount}
                helperText={errors?.amount}
                inputProps={{ min: 0 }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Grid>

            {/* Type */}
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="Type"
                select
                variant="outlined"
                fullWidth
                value={type}
                onChange={(e) => setType(e.target.value)}
                error={!!errors?.type}
                helperText={errors?.type}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              >
                {TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Category - shown only for Treatment type */}
            {type === "Treatment" && (
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  label="Category"
                  select
                  variant="outlined"
                  fullWidth
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  error={!!errors?.category}
                  helperText={errors?.category}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                >
                  {categoriesLoading ? (
                    <MenuItem disabled value="">
                      Loading...
                    </MenuItem>
                  ) : (
                    categories.map((cat) => (
                      <MenuItem key={cat._id} value={cat._id}>
                        {cat.name}
                      </MenuItem>
                    ))
                  )}
                </TextField>
              </Grid>
            )}

            {/* Currency */}
            <Grid item xs={12} sm={type === "Treatment" ? 12 : 6}>
              <TextField
                margin="dense"
                label="Currency"
                select
                variant="outlined"
                fullWidth
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                error={!!errors?.currency}
                helperText={errors?.currency}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              >
                {CURRENCIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Related Shop */}
            <Grid item xs={12}>
              <Autocomplete
                options={shops}
                loading={shopsLoading}
                value={relatedShop}
                onChange={(_, val) => {
                  setRelatedShop(val);
                  if (val)
                    setErrors((prev) => ({ ...prev, relatedShop: undefined }));
                }}
                getOptionLabel={getShopLabel}
                isOptionEqualToValue={(opt, val) =>
                  (opt?._id || opt?.id) === (val?._id || val?.id)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="dense"
                    label="Related Shop *"
                    variant="outlined"
                    placeholder="Select a shop..."
                    error={!!errors?.relatedShop}
                    helperText={errors?.relatedShop}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {shopsLoading ? <CircularProgress size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  />
                )}
                noOptionsText={shopsLoading ? "Loading..." : "No shops found"}
                clearOnEscape
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                margin="dense"
                label="Description"
                multiline
                rows={3}
                variant="outlined"
                fullWidth
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2.5,
          borderTop: "1px solid #eee",
          bgcolor: "#f8f9fa",
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
        }}
      >
        <Button
          onClick={handleClose}
          disabled={isSubmitting}
          color="inherit"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            px: 3,
          }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form="point-rule-form"
          variant="contained"
          color="primary"
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            px: 4,
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(25, 118, 210, 0.2)",
            "&:hover": { boxShadow: "0 6px 16px rgba(25, 118, 210, 0.3)" },
          }}
        >
          {editData ? "Update Rule" : "Create Rule"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
